import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../lib/api';
import { AuditStatus, BlockchainNetwork } from './useSmartContractTemplates';

export interface DeployPreviewDto {
  network: BlockchainNetwork;
  params: {
    name: string;
    symbol: string;
    decimals?: number;
    initialSupply?: string;
    owner?: string;
    [key: string]: any;
  };
}

export interface GasPrice {
  slow: string;
  standard: string;
  fast: string;
  unit: string;
}

export interface DeployCost {
  slow: string;
  standard: string;
  fast: string;
  currency: string;
}

export interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

export interface NetworkConfig {
  chainId: number;
  rpcUrl: string;
  explorerUrl: string;
  nativeCurrency: string;
}

export interface DeployPreview {
  templateId: string;
  templateName: string;
  templateVersion: string;
  network: BlockchainNetwork;
  networkConfig: NetworkConfig;
  params: Record<string, any>;
  finalCode: string;
  bytecode: string;
  abi: any[];
  gasEstimate: string;
  gasPrice: GasPrice;
  estimatedCost: DeployCost;
  validationErrors: ValidationError[];
  canDeploy: boolean;
  auditStatus: AuditStatus;
  warnings: string[];
}

export interface SupportedNetwork {
  network: string;
  config: {
    chainId: number;
    rpcUrl: string;
    explorerUrl: string;
    nativeCurrency: string;
    isMainnet: boolean;
  };
}

// API Functions
const generatePreview = async ({ templateId, dto }: { templateId: string; dto: DeployPreviewDto }): Promise<DeployPreview> => {
  const response = await api.post(`/tokenization/smart-contracts/templates/${templateId}/preview`, dto);
  return response.data;
};

const getCodeWithParams = async ({ templateId, params }: { templateId: string; params: Record<string, any> }): Promise<string> => {
  const response = await api.post(`/tokenization/smart-contracts/templates/${templateId}/code/preview`, params);
  return response.data.code;
};

const fetchSupportedNetworks = async (): Promise<SupportedNetwork[]> => {
  const response = await api.get('/tokenization/smart-contracts/networks');
  return response.data;
};

const fetchMainnetNetworks = async (): Promise<string[]> => {
  const response = await api.get('/tokenization/smart-contracts/networks/mainnet');
  return response.data;
};

const fetchTestnetNetworks = async (): Promise<string[]> => {
  const response = await api.get('/tokenization/smart-contracts/networks/testnet');
  return response.data;
};

// Hooks
export function useGeneratePreview() {
  return useMutation({
    mutationFn: generatePreview,
  });
}

export function useCodeWithParams() {
  return useMutation({
    mutationFn: getCodeWithParams,
  });
}

export function useSupportedNetworks() {
  return useQuery({
    queryKey: ['supported-networks'],
    queryFn: fetchSupportedNetworks,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

export function useMainnetNetworks() {
  return useQuery({
    queryKey: ['mainnet-networks'],
    queryFn: fetchMainnetNetworks,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

export function useTestnetNetworks() {
  return useQuery({
    queryKey: ['testnet-networks'],
    queryFn: fetchTestnetNetworks,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

// Export all hooks as a single object
export const useDeployPreview = {
  useGeneratePreview,
  useCodeWithParams,
  useSupportedNetworks,
  useMainnetNetworks,
  useTestnetNetworks,
};

export default useDeployPreview;
